import { Menu } from "@base-ui/react/menu";
import type { ReactElement, ReactNode } from "react";
import { cn } from "../cn";

// A menu of actions under a button (ADR-050, point 3): the "More" of a toolbar that does not fit, the user menu of
// the top bar. The items run the same functions as the buttons they replace.

export interface MenuAction {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
  destructive?: boolean;
  disabled?: boolean;
  onSelect: () => void;
}

interface DropdownMenuProps {
  /** The button that opens the menu, e.g. `<Button aria-label="More">`. */
  trigger: ReactElement;
  items: MenuAction[];
  /** Text above the items (the user's name and organization). */
  header?: ReactNode;
  align?: "start" | "end";
  /** Controlled, for a menu a key opens too (`/` on the canvas of an email); uncontrolled without. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function DropdownMenu({ trigger, items, header, align = "end", open, onOpenChange }: DropdownMenuProps) {
  return (
    <Menu.Root {...(open !== undefined && { open })} {...(onOpenChange && { onOpenChange })}>
      <Menu.Trigger render={trigger} />
      <Menu.Portal>
        <Menu.Positioner side="bottom" align={align} sideOffset={4} className="z-30">
          <Menu.Popup className="min-w-44 max-w-[calc(100vw-1rem)] rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-popover outline-none">
            {header && <div className="border-b border-border px-2.5 pt-1.5 pb-2 text-xs">{header}</div>}
            {items.map((item) => (
              <Menu.Item
                key={item.id}
                disabled={item.disabled ?? false}
                onClick={item.onSelect}
                className={cn(
                  "flex h-(--control-h) cursor-default items-center gap-2 rounded-md px-2.5 text-sm outline-none select-none data-[disabled]:opacity-50 data-[highlighted]:bg-accent [&_svg]:size-4 [&_svg]:text-muted-foreground",
                  item.destructive && "text-destructive [&_svg]:text-destructive",
                )}
              >
                {item.icon}
                {item.label}
              </Menu.Item>
            ))}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}
